import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, Link2, Unlink, Eraser } from "lucide-react";

// Lightweight rich text editor for article text blocks. Applies inline
// formatting (bold, italic, underline, link) to the highlighted selection
// and outputs clean HTML that the public article renderer already styles.
export default function RichTextEditor({ value, onChange, placeholder = "Write a paragraph. Highlight text to format it.", compact = false }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };

  const focusPara = () => {
    try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch (e) {}
  };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    try { document.execCommand(cmd, false, arg); } catch (e) {}
    emit();
  };

  const addLink = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) { window.alert("Highlight the text you want to turn into a link first."); return; }
    let url = window.prompt("Link URL (include https://)", "https://");
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !url.startsWith("/") && !url.startsWith("#") && !url.startsWith("mailto:")) url = "https://" + url;
    exec("createLink", url);
  };

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-brand hover:bg-ink-700 rounded transition-colors">
      {children}
    </button>
  );

  return (
    <div className="rounded-lg border border-ink-600 overflow-hidden bg-ink-800 focus-within:border-brand transition-colors">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-ink-600 bg-ink-900">
        <Btn onClick={() => exec("bold")} title="Bold" testid="rte-bold"><Bold size={15} /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" testid="rte-italic"><Italic size={15} /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" testid="rte-underline"><Underline size={15} /></Btn>
        <span className="w-px h-5 bg-ink-600 mx-1" />
        <Btn onClick={addLink} title="Add link" testid="rte-link"><Link2 size={15} /></Btn>
        <Btn onClick={() => exec("unlink")} title="Remove link" testid="rte-unlink"><Unlink size={15} /></Btn>
        <span className="w-px h-5 bg-ink-600 mx-1" />
        <Btn onClick={() => exec("removeFormat")} title="Clear formatting" testid="rte-clear"><Eraser size={15} /></Btn>
      </div>
      <div
        ref={ref}
        className={`rte thin-scroll ${compact ? "rte-compact" : ""}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        data-testid="rte-editable"
        onFocus={focusPara}
        onInput={emit}
        onBlur={emit}
      />
    </div>
  );
}
